import * as React from "react";
import { ActionType } from "../../../../../react-core/Flux/ActionType";
import { CommonHelper } from "../../../../../react-core/Helpers/CommonHelper";
import { BaseComponent } from "../../../../../react-core/Html/BaseComponent";
import { SelectEditor } from "../../../../../react-core/Html/SelectEditor";
import { DividendReinvestment } from "../../../../../Wrap/Constants/Enums/Domain/DividendReinvestment";
import { InvestmentType } from "../../../../../Wrap/Constants/Enums/InvestmentType";
import { ManagedFundsRebateType } from "../../../../../Wrap/Constants/Enums/ManagedFundsRebateType";
import { Investment } from "../../../../../Wrap/Models/Models/PortfolioAnalysis/Investment/Investment";
import { InvestmentSelectionAction } from "../../../../Shared/IST/Action/InvestmentSelectionAction";
import { FavouritesToggle } from "../../../../Shared/IST/Components/Shared/FavouritesToggle";
import { FundType } from "../../../../Shared/IST/Resource/Content";
import { Tab } from "../../../../Shared/Tab/Tab";
import { Table } from "../../../../Shared/Table/Table";
import { InvestmentDetailsPopupModal } from "../../Clients/Common/InvestmentDetailsPopUp/InvestmentDetailsPopupModal";
import { FundDataType, ManagedFundsRebateTypeMap } from "../Resource/Content";
import { ResearchISTStore } from "../Store/Variable";
import { uniqBy } from "lodash";

interface IResearchResultTableProps {
    tab: InvestmentType;
}

interface IResearchResultTableState {
    data: any;
    fundCode: any;
    exchangeTradeCode: any;
    curPage: number;
    selectedInvestment: Investment;
    isShowDetailModal: boolean;
    currentFundDataType: FundDataType;
}

export class ResearchResultTable extends BaseComponent<IResearchResultTableProps, IResearchResultTableState> {
    constructor(props: IResearchResultTableProps) {
        super(props);
        this.state = {
            data: null,
            fundCode: { key: "ProductCode", value: "Fund code" },
            exchangeTradeCode: { key: "ProductCode", value: "Code" },
            curPage: 1,
            isShowDetailModal: false,
            selectedInvestment: null,
            currentFundDataType: FundDataType.Overview,
        };
        this.subscribeToStore(ResearchISTStore, () => this.onStoreChange());
    }

    private onStoreChange() {
        if (ResearchISTStore.getActionType() == ActionType.Success) {
            this.setState({ data: ResearchISTStore.getData() });
            if (ResearchISTStore.getIsBasicSearch()) {
                this.setState({ curPage: 1 });
            }
        }
        if (ResearchISTStore.getActionType() == ActionType.Update) {
            this.setState({ curPage: 1 });
        }
    }

    private codeFundOptions = [
        { key: "ProductCode", value: "Fund code" },
        { key: "Isin", value: "ISIN" },
        { key: "Sedol", value: "SEDOL" },
        { key: "MexId", value: "Mex ID" },
    ];

    renderInvestmentDetailPopupModal = (): JSX.Element => {
        const { isShowDetailModal, selectedInvestment } = this.state;
        if (!isShowDetailModal) {return null;}
        return (
            <>
                <InvestmentDetailsPopupModal
                    showBox={isShowDetailModal}
                    callBack={() => {
                        this.setState({ isShowDetailModal: false });
                    }}
                    instrumentCode={selectedInvestment?.InstrumentCode}
                    productId={selectedInvestment?.ProductId}
                    instrumentName={selectedInvestment?.DisplayName}
                    unitCost={selectedInvestment?.UnitCost}
                    securityType={selectedInvestment?.Type}
                    unitPrice={selectedInvestment?.UnitPrice}
                />
            </>
        );
    };

    onInvestmentSelected = (investment) => {
        InvestmentSelectionAction.getUnitPrice(investment, (d) => {
            this.setState({ selectedInvestment: d, isShowDetailModal: true });
        });
    };

    private getTable = () => {
        const { fundCode, currentFundDataType } = this.state;

        const result = [];
        result.push({
            title: "Fund name",
            key: "Name",
            render: (data, row, index) => {
                return (
                    <FavouritesToggle
                        cssWrapper="in-table"
                        label={
                            <a
                                className="hyperlink"
                                onClick={(ele) => {
                                    ele.stopPropagation();
                                    this.onInvestmentSelected(row);
                                }}
                            >
                                {CommonHelper.isNullOrEmpty(data) ? "-" : data}
                            </a>
                        }
                        onToggle={(v) => {
                            InvestmentSelectionAction.setFavouriteProdcut(row.ProductId, v);
                            let product = this.state.data?.Results?.find((ele) => ele.ProductId == row.ProductId);
                            if (!product) {
                                product = this.state.data?.find((ele) => ele.ProductId == row.ProductId);
                            }
                            product.IsFavourite = v;
                            this.setState({ data: this.state.data });
                        }}
                        isFavourite={row.IsFavourite}
                        key={data}
                    />
                );
            },
        });
        if (currentFundDataType == FundDataType.Overview) {
            result.push(
                {
                    title: (
                        <SelectEditor
                            options={this.codeFundOptions}
                            getText={(v) => v.value}
                            selected={fundCode}
                            onChange={(v) => this.setState({ fundCode: v })}
                            cssWrapper="mb-0 select-in-table"
                        />
                    ),
                    key: fundCode.key,
                    render: (data) => {
                        return CommonHelper.isNullOrEmpty(data) ? "-" : data;
                    },
                },
                {
                    title: "Fund type",
                    key: "DividendReinvestment",
                    render: (data) => {
                        return CommonHelper.isNullOrEmpty(data) || data == DividendReinvestment.NotSpecified
                            ? "-"
                            : FundType[data];
                    },
                },
                {
                    title: "Cleanest share class",
                    key: "CleanestShareClass",
                    render: (data) => {
                        return CommonHelper.isNullOrEmpty(data) || data == ManagedFundsRebateType.NotSpecified
                            ? "-"
                            : ManagedFundsRebateTypeMap[data];
                    },
                },
                {
                    title: "Asset class",
                    key: "AssetClass",
                    render: (data) => {
                        return CommonHelper.isNullOrEmpty(data) ? "-" : data;
                    },
                }
            );
        } else if (currentFundDataType == FundDataType.Charges) {
            result.push(
                {
                    title: "AMC",
                    key: "Amc",
                    render: (data) => {
                        return data.toFixed(2) + "%";
                    },
                },
                {
                    title: "OCF",
                    key: "Ocf",
                    render: (data) => {
                        return data.toFixed(2) + "%";
                    },
                },
                {
                    title: "Initial charge",
                    key: "InitialCharge",
                    render: (data) => {
                        return data.toFixed(2) + "%";
                    },
                },
                {
                    title: "Transaction costs",
                    key: "TransactionCosts",
                    render: (data) => {
                        return data.toFixed(2) + "%";
                    },
                }
            );
        } else if (currentFundDataType == FundDataType.Tradeable) {
            result.push(
                {
                    title: "SIPP",
                    key: "SippEnabled",
                    render: (data) => {
                        return data ? "Yes" : "No";
                    },
                },
                {
                    title: "ISA stocks shares",
                    key: "StocksIsaEnabled",
                    render: (data) => {
                        return data ? "Yes" : "No";
                    },
                },
                {
                    title: "ISA cash",
                    key: "CashIsaEnabled",
                    render: (data) => {
                        return data ? "Yes" : "No";
                    },
                },
                {
                    title: "Personal portfolio",
                    key: "PersonalPortfolioEnabled",
                    render: (data) => {
                        return data ? "Yes" : "No";
                    },
                },
                {
                    title: "Onshore bond",
                    key: "OnshoreBondEnabled",
                    render: (data) => {
                        return data ? "Yes" : "No";
                    },
                },
                {
                    title: "Offshore bond",
                    key: "OffshoreBondEnabled",
                    render: (data) => {
                        return data ? "Yes" : "No";
                    },
                }
            );
        }
        return result;
    };

    render() {
        const { data, curPage } = this.state;
        if (!data) {return null;}
        return (
            <div className="panel pr-0 pl-0 pb-0 pt-0 mt-40">
                <h3>Selection</h3>
                <p>
                    {this.props.tab == InvestmentType.Funds ? "Funds available" : "Instruments available"}:{" "}
                    {data.Pager ? data.Pager.TotalRecords : data.length}
                </p>
                <Tab
                    tabs={FundDataType}
                    curTabIndex={this.state.currentFundDataType}
                    onClick={(tab) => {
                        this.setState({ currentFundDataType: tab });
                    }}
                />

                <Table
                    data={
                        data.Results
                            ? uniqBy(data.Results, "ProductId")
                            : uniqBy(data, "ProductId").slice((curPage - 1) * 10, curPage * 10)
                    }
                    cssWrapper={"no-sided-padding has-favourite-toggle"}
                    showEmptyMsg
                    cssEmptyMessage="warning-message mt-20"
                    needPagination={{
                        totalNumOfResult: data.Pager ? data.Pager.TotalRecords : uniqBy(data, "ProductId").length,
                        currentPage: curPage,
                        onPaginationChange: (pageIndex) => {
                            if (data.Pager) {
                                this.setState({ curPage: pageIndex });
                                ResearchISTStore.search(pageIndex);
                            } else {
                                this.setState({ curPage: pageIndex });
                            }
                        },
                    }}
                    col={this.getTable()}
                />
                {this.renderInvestmentDetailPopupModal()}
            </div>
        );
    }
}
